import { differenceInDays, parseISO, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { membershipRuleKeys } from '../../constants/membership';
import { Event } from '../../types/event';
import { Ticket } from '../../types/ticket';
import { Rule } from '../types/Rule';
import { RuleContext } from '../types/RuleContext';

export class CheckRatingBasedTicketAccessRule implements Rule<Ticket[]> {
  private static readonly RATING_DAYS_MAP = {
    1 : 7,
    2 : 6,
    3 : 5,
  } as const;

  private static readonly DEFAULT_RATING = 3;
  private static readonly ESQUADRAO_PREFIX = 'ESQUADRAONAFONTE';
  private static readonly ACCESS_HOUR = 10;

  apply(context: RuleContext): Ticket[] {
    const { tickets, membership, event, updateMembershipError } = context;

    // Only apply when we have membership data and tickets
    if (!membership || !tickets) {
      return tickets;
    }

    if (!this.isEsquadraoNaFonte(membership.name)) {
      return tickets;
    }

    const referenceDate = this.getDateInEventTimezone(membership.timestamp!, event);

    // Filter tickets and check if user has access
    const hasAccess = this.hasAccessToTicketDates(event, membership.rating!, referenceDate);

    if(!hasAccess) {
      updateMembershipError?.(membershipRuleKeys.priorityRestricted);
      return [];
    }

    return tickets;
  }

  private isEsquadraoNaFonte(membershipName: string): boolean {
    return membershipName?.startsWith(CheckRatingBasedTicketAccessRule.ESQUADRAO_PREFIX);
  }

  private getDateInEventTimezone(date: string, event: Event): Date {
    if (!date) {
      return new Date();
    }

    // Ensure the date string is treated as UTC by appending 'Z' if not present
    const utcDateString = date.endsWith('Z') ? date : `${date}Z`;

    // Parse as UTC date
    const membershipDateTime = parseISO(utcDateString);
    const eventTimezone = event?.timezone || 'America/Sao_Paulo';

    // Convert the UTC date to the event's timezone
    return toZonedTime(membershipDateTime, eventTimezone);
  }

  private hasAccessToTicketDates(
    event: Event,
    rating: number,
    referenceDate: Date
  ): boolean {
    return event.sessions.map((session) => {
      const sessionDate = this.getDateInEventTimezone(session.dateTime, event);

      // Normalize both dates to start of day for accurate day counting
      const sessionStartOfDay = startOfDay(sessionDate);
      const referenceStartOfDay = startOfDay(referenceDate);

      const daysUntilEvent = differenceInDays(sessionStartOfDay, referenceStartOfDay);

      const hasAccess = daysUntilEvent >= 0 &&
        this.hasAccessBasedOnRatingAndTime(rating, daysUntilEvent, referenceDate);

      return hasAccess;
    }).every((hasAccess) => hasAccess);
  }

  private hasAccessBasedOnRatingAndTime(rating: number, daysUntilEvent: number, referenceDate: Date): boolean {
    const allowedDays = CheckRatingBasedTicketAccessRule.RATING_DAYS_MAP[
      rating as keyof typeof CheckRatingBasedTicketAccessRule.RATING_DAYS_MAP
    ] || CheckRatingBasedTicketAccessRule.RATING_DAYS_MAP[CheckRatingBasedTicketAccessRule.DEFAULT_RATING];

    // If we're more than allowed days away, no access
    if (daysUntilEvent > allowedDays) {
      return false;
    }

    // If we're less than allowed days away, always have access
    if (daysUntilEvent < allowedDays) {
      return true;
    }

    // If we're exactly at the allowed days limit, check time
    // For ratings 2 and 3, only allow access after 10:00 AM on the allowed day
    if (daysUntilEvent === allowedDays && (rating === 2 || rating === 3)) {
      const currentHour = referenceDate.getHours();
      return currentHour >= CheckRatingBasedTicketAccessRule.ACCESS_HOUR;
    }

    // Rating 1 has full access on their allowed day
    return true;
  }

}
